import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  DataTable, Drawer, EmptyState, ErrorState, Feld, FormSection, LoadingState,
  type Spalte,
} from '../muster';

export function Projekte({ api }: { api: ApiClient }) {
  const [projekte, setProjekte] = useState<Projekt[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [anlegen, setAnlegen] = useState(false);
  const navigiere = useNavigate();

  const laden = () => {
    setFehler(null);
    return api.projekte()
      .then(setProjekte)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  };

  useEffect(() => { laden(); }, [api]);

  const spalten: Spalte<Projekt>[] = [
    {
      schluessel: 'number', titel: 'Nummer', breite: '160px', immerSichtbar: true,
      wert: (p) => p.number,
      inhalt: (p) => <strong>{p.number}</strong>,
    },
    { schluessel: 'name', titel: 'Name', wert: (p) => p.name },
  ];

  return (
    <>
      <PageHeader
        titel="Projekte"
        beschreibung="Alle Projekte der Plattform. Ein Klick öffnet die Projektübersicht."
        meta={projekte ? `${projekte.length} Projekt(e)` : undefined}
        aktionen={(
          <button type="button" className="m-knopf m-knopf-haupt"
            onClick={() => setAnlegen(true)}>
            Projekt anlegen
          </button>
        )}
      />

      {fehler && (
        <ErrorState
          meldung="Die Projektliste konnte nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}

      {!projekte && !fehler && <LoadingState text="Projekte werden geladen …" zeilen={6} spalten={2} />}

      {projekte && (
        <DataTable
          name="Projekte"
          spalten={spalten}
          zeilen={projekte}
          schluessel={(p) => p.id}
          ausfuhr
          ausfuhrDateiname="projekte"
          beiZeileKlick={(p) => navigiere(`/projekte/${p.id}`)}
          leer={(
            <EmptyState
              titel="Noch kein Projekt angelegt."
              erklaerung={'Ein Projekt verbindet die Revit-Modelle aus ACC mit den '
                + 'Fachbereichen dieser Plattform.'}
              aktion={(
                <button type="button" className="m-knopf m-knopf-haupt"
                  onClick={() => setAnlegen(true)}>
                  Projekt anlegen
                </button>
              )}
            />
          )}
        />
      )}

      <ProjektAnlegen
        api={api}
        offen={anlegen}
        schliessen={() => setAnlegen(false)}
        fertig={(id) => { setAnlegen(false); navigiere(`/projekte/${id}`); }}
      />
    </>
  );
}

function ProjektAnlegen({
  api, offen, schliessen, fertig,
}: {
  api: ApiClient;
  offen: boolean;
  schliessen: () => void;
  fertig: (id: string) => void;
}) {
  const [nummer, setNummer] = useState('');
  const [name, setName] = useState('');
  const [region, setRegion] = useState('EMEA');
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      const ergebnis = await api.projektAnlegen({
        number: nummer.trim(),
        name: name.trim() || nummer.trim(),
        accRegion: region,
      });
      fertig(ergebnis.id);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
      setLaeuft(false);
    }
  };

  return (
    <Drawer
      offen={offen}
      titel="Neues Projekt"
      beschreibung="Projektnummer, Name und die Region des ACC-Kontos."
      beiSchliessen={schliessen}
      fuss={(
        <>
          <button type="button" className="m-knopf m-knopf-haupt"
            onClick={() => void speichern()} disabled={!nummer.trim() || laeuft}>
            {laeuft ? 'wird angelegt …' : 'Anlegen'}
          </button>
          <button type="button" className="m-knopf" onClick={schliessen}>Abbrechen</button>
        </>
      )}
    >
      <FormSection titel="Stammdaten" spalten={1}>
        <Feld beschriftung="Projektnummer" wert={nummer} pflicht platzhalter="2026-041"
          beiAenderung={(w) => setNummer(w as string)} />
        <Feld beschriftung="Name" wert={name} platzhalter="Campus Munich Arena"
          hinweis="Leer übernimmt die Projektnummer."
          beiAenderung={(w) => setName(w as string)} />
        <Feld art="auswahl" beschriftung="ACC-Region" wert={region}
          optionen={[
            { wert: 'EMEA', beschriftung: 'EMEA' },
            { wert: 'US', beschriftung: 'US' },
          ]}
          hinweis={'Richtet sich danach, wo das ACC-Konto eingerichtet wurde — nicht nach '
            + 'dem Standort des Projekts. Bei TEUBER+VIEL ist es US.'}
          beiAenderung={(w) => setRegion(w as string)} />
      </FormSection>

      {fehler && (
        <ErrorState
          klein
          meldung="Das Projekt konnte nicht angelegt werden."
          technisch={fehler}
          beiErneutVersuchen={() => void speichern()}
        />
      )}
    </Drawer>
  );
}

/**
 * Die Fehleranzeige, die zwei Dutzend Seiten benutzen.
 *
 * Umzug U6c: innen `ErrorState` der Musterbibliothek (Auftrag Abschnitt 21 —
 * verständliche Meldung, `[Erneut versuchen]`, technische Einzelheiten
 * aufklappbar). Außen unverändert, damit die aufrufenden Seiten der Züge
 * U6a und U6b nichts ändern müssen.
 *
 * Der rohe Text kommt unter „Technische Einzelheiten": Seit dem 20.08.2026
 * hängt die API an eine Fehlermeldung die Abhilfe an, durch eine Leerzeile
 * getrennt („Für LA-01 liegt keine Druckverlustberechnung vor." / „Erst die
 * Druckverlustberechnung laufen lassen.") — `ErrorState` hält den Umbruch.
 */
export function Fehler({ text }: { text: string }) {
  return <ErrorState meldung={text.split('\n\n')[0]} technisch={text} />;
}
